import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Plus, Loader2, LogIn, Pencil, Ban, CheckCircle2, Upload, X,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";

const EMPTY = {
  name: "", legal_name: "", rfc: "", tax_regime: "", address: "", facturapi_key: "",
  admin_name: "", admin_email: "", admin_password: "",
};

const In = ({ label, value, onChange, type = "text", required = true, testId, placeholder = "" }) => (
  <div>
    <label className="label-mono block mb-1.5">{label}</label>
    <input data-testid={testId} type={type} value={value} required={required} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} className="input-cyber px-3 py-2 text-sm w-full" />
  </div>
);

export default function Companies() {
  const { user, company: activeCompany, switchCompany } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && !user.superadmin) { navigate("/dashboard"); return; }
    load();
  }, [user]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", legal_name: c.legal_name || "", rfc: c.rfc || "",
      tax_regime: c.tax_regime || "", address: c.address || "", facturapi_key: "",
      admin_name: "", admin_email: "", admin_password: "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name, legal_name: form.legal_name, rfc: form.rfc,
          tax_regime: form.tax_regime, address: form.address,
        };
        if (form.facturapi_key.trim()) payload.facturapi_key = form.facturapi_key.trim();
        await api.put(`/companies/${editing.id}`, payload);
        toast.success("Empresa actualizada");
      } else {
        await api.post("/companies", form);
        toast.success("Empresa creada. El administrador ya puede iniciar sesión.");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    try {
      const status = c.status === "active" ? "suspended" : "active";
      await api.put(`/companies/${c.id}`, { status });
      toast.success(status === "active" ? "Empresa activada" : "Empresa suspendida");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadLogo = async (c, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/companies/${c.id}/logo`, fd);
      toast.success("Logo actualizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const enter = async (c) => {
    try {
      await switchCompany(c.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-4 lg:p-8 space-y-6" data-testid="companies-page">
      <PageHeader
        title="Empresas"
        subtitle="GESTIÓN MULTI-EMPRESA · SUPERADMIN"
        actions={
          <button data-testid="new-company-btn" onClick={openCreate}
            className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nueva empresa
          </button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {companies.map((c) => {
            const isCurrent = activeCompany?.id === c.id;
            return (
              <div key={c.id} data-testid={`company-card-${c.id}`} className="panel p-5 space-y-3">
                <div className="flex items-start gap-3">
                  {c.logo_b64 ? (
                    <img src={c.logo_b64} alt={c.name} className="w-11 h-11 object-contain rounded-sm" style={{ background: "rgba(255,255,255,0.04)" }} />
                  ) : (
                    <div className="w-11 h-11 flex items-center justify-center rounded-sm"
                      style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.25)" }}>
                      <Building2 className="w-5 h-5 text-cyan-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-500 label-mono truncate">{c.rfc || "SIN RFC"}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {c.is_default && <span className="badge-info text-[10px] px-2 py-0.5 rounded-sm">PRINCIPAL</span>}
                    {isCurrent && <span className="badge-success text-[10px] px-2 py-0.5 rounded-sm" data-testid={`company-current-${c.id}`}>ACTIVA</span>}
                    {c.status === "suspended" && <span className="badge-danger text-[10px] px-2 py-0.5 rounded-sm">SUSPENDIDA</span>}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="panel p-2" style={{ background: "rgba(8,12,24,0.6)" }}>
                    <div className="text-lg font-bold text-cyan-300">{c.users_count}</div>
                    <div className="text-[10px] text-slate-500 label-mono">USUARIOS</div>
                  </div>
                  <div className="panel p-2" style={{ background: "rgba(8,12,24,0.6)" }}>
                    <div className="text-lg font-bold text-slate-200">{c.projects_count}</div>
                    <div className="text-[10px] text-slate-500 label-mono">OBRAS</div>
                  </div>
                  <div className="panel p-2" style={{ background: "rgba(8,12,24,0.6)" }}>
                    <div className={`text-lg font-bold ${c.has_facturapi_key || c.is_default ? "text-emerald-400" : "text-slate-600"}`}>
                      {c.has_facturapi_key || c.is_default ? "SÍ" : "NO"}
                    </div>
                    <div className="text-[10px] text-slate-500 label-mono">CFDI</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  {!isCurrent && c.status === "active" && (
                    <button data-testid={`company-enter-${c.id}`} onClick={() => enter(c)}
                      className="btn-primary px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm flex items-center gap-1.5">
                      <LogIn className="w-3.5 h-3.5" /> Entrar
                    </button>
                  )}
                  <button data-testid={`company-edit-${c.id}`} onClick={() => openEdit(c)}
                    className="btn-ghost px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm flex items-center gap-1.5">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <label className="btn-ghost px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm flex items-center gap-1.5 cursor-pointer">
                    <Upload className="w-3.5 h-3.5" /> Logo
                    <input data-testid={`company-logo-input-${c.id}`} type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadLogo(c, e.target.files?.[0])} />
                  </label>
                  {!c.is_default && (
                    <button data-testid={`company-toggle-${c.id}`} onClick={() => toggleStatus(c)}
                      className="btn-ghost px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm flex items-center gap-1.5"
                      style={{ color: c.status === "active" ? "#f87171" : "#34d399" }}>
                      {c.status === "active" ? <><Ban className="w-3.5 h-3.5" /> Suspender</> : <><CheckCircle2 className="w-3.5 h-3.5" /> Activar</>}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8 px-4"
          style={{ background: "rgba(5,8,18,0.8)", backdropFilter: "blur(6px)" }}>
          <form onSubmit={submit} className="panel w-full max-w-xl p-6 space-y-4" data-testid="company-form">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-mono" style={{ color: "var(--cyan)" }}>// {editing ? "EDITAR" : "NUEVA"} EMPRESA</div>
                <h2 className="text-lg font-semibold text-slate-100">{editing ? editing.name : "Registrar empresa"}</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <In label="Nombre comercial" testId="company-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <In label="Razón social" testId="company-legal" required={false} value={form.legal_name} onChange={(v) => setForm({ ...form, legal_name: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="RFC" testId="company-rfc" required={false} value={form.rfc} onChange={(v) => setForm({ ...form, rfc: v })} />
              <In label="Régimen fiscal" testId="company-regime" required={false} value={form.tax_regime} onChange={(v) => setForm({ ...form, tax_regime: v })} placeholder="601 General de Ley PM" />
            </div>
            <In label="Dirección fiscal" testId="company-address" required={false} value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            <In label={editing ? "Llave Facturapi (dejar vacío para no cambiar)" : "Llave Facturapi (opcional)"} testId="company-facturapi"
              required={false} value={form.facturapi_key} onChange={(v) => setForm({ ...form, facturapi_key: v })} placeholder="sk_test_... o sk_live_..." />

            {!editing && (
              <div className="panel p-3 space-y-3" style={{ background: "rgba(8,12,24,0.6)" }}>
                <span className="label-mono" style={{ color: "var(--magenta)" }}>// ADMINISTRADOR DE LA EMPRESA</span>
                <div className="grid grid-cols-2 gap-3">
                  <In label="Nombre" testId="company-admin-name" value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} />
                  <In label="Correo" testId="company-admin-email" type="email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} />
                </div>
                <In label="Contraseña (mín. 8 caracteres)" testId="company-admin-password" type="password" value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} />
              </div>
            )}

            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="save-company-btn"
                className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
